"use client";

import { useRef, useState, useEffect } from "react";

type ButtonSlotProps = {
  children: React.ReactNode;
  className?: string;
};

/**
 * Wrapper that auto-collapses when its child SDK button renders empty.
 * Uses ResizeObserver to detect when content has zero height,
 * preventing ghost grid slots from payment SDK timing mismatches.
 */
export default function ButtonSlot({ children, className }: ButtonSlotProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [hasVisibleContent, setHasVisibleContent] = useState(true);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const checkVisibility = () => {
      const height = el.scrollHeight;
      setHasVisibleContent(height > 0);
    };

    // Defer initial check one frame so skeletons/SDK buttons have time to mount
    const raf = requestAnimationFrame(checkVisibility);

    const observer = new ResizeObserver(checkVisibility);
    observer.observe(el);

    // Also observe mutations for SDK buttons that inject DOM async
    const mutationObserver = new MutationObserver(checkVisibility);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  // Hide via CSS instead of returning null so observers stay attached
  // and can re-expand if SDK content loads after initial collapse
  return (
    <div
      className={className}
      style={hasVisibleContent ? undefined : { display: "none" }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
